import React from 'react';
import { AlertTriangle, Check, Clock, Receipt, ShieldCheck, TriangleAlert } from 'lucide-react';
import {
  Alert, Checkbox, ConfirmDialog, ErrorState, SkeletonRegion, SkeletonText, Spinner, useResponsive,
} from '@parsina/framework';
import { C, RADIUS, soft } from '@parsina/framework/tokens';
import { isIranMarket } from '@parsina/framework/ui/lib/brand';

import { useTranslation } from '../../i18n';
import { STACK } from '../../constants/density';
import { StepHeading, PrimaryAction } from '../../components/auth/OnboardingParts.jsx';
import { useShortcut, Kbd } from '../../lib/shortcuts.jsx';
import { bffQuery, bffMutation } from '../../lib/bffClient.js';
import { PERMISSION_CATALOG_QUERY } from '../People/constants.js';
import { roleTemplateLabel } from '../People/permissionLabels';
import PresenceEditor from '../People/components/PresenceEditor.jsx';
import VisitDurationField from '../People/components/VisitDurationField.jsx';
import { presenceIssues, toPresenceInput } from '../People/personnel.js';
import { DURATION_MIN, DURATION_MAX } from '../Booking/constants';
import { MoneyField } from '../../components/common/fields';
import { DEFAULT_CURRENCY } from '../../lib/currency';
import { describeError, RECOVERY } from './errors.js';
import {
  DEFAULT_PRESENCE_DAYS, DEFAULT_SHIFT, DEFAULT_VISIT_MINUTES, DEFAULT_VISIT_PRICE_TOMAN,
  defaultDoctorSetup, defaultPresences, visitDeclarationInput,
} from './parsinaDefaults.js';

/**
 * The last step of registration — «راه‌اندازی اولیه».
 *
 * ── «تنظیمات پیش‌فرض پارسینا» ────────────────────────────────────────────────
 *
 * The step opens with one tick, ON: every standard role is seeded and, on the
 * doctor track, the founder-doctor gets the default week and visit length
 * (`parsinaDefaults.js`). Taking the tick off opens the same choices as editors
 * — the role tiles, and for a doctor the presence week and the visit length —
 * each starting FROM the defaults, so a founder who wants Thursdays too adds a
 * day rather than building a week from nothing.
 *
 * «ویزیت» is part of the defaults too: the standard visit service, priced at
 * DEFAULT_VISIT_PRICE_TOMAN, or at whatever the founder types with the tick off.
 * This step DECLARES it — clinic-wide, and inactive, because accounting refuses
 * an active service with no performer and nobody performs anything yet.
 *
 * The doctor's settings are only COLLECTED here and handed to `onDone`. They
 * are written by the wizard after the founder is joined to the clinic, because
 * the membership row they live on does not exist until that join has run — and
 * so is the founder's name on «ویزیت», which is what switches it on.
 *
 * ── The roles half ──────────────────────────────────────────────────────────
 *
 * The clinic declares WHICH JOBS EXIST in it at the moment it is created,
 * rather than discovering later that nobody can be given one. The platform
 * ships five role templates (منشی، پرستار، حسابدار، مسئول انبار، مدیر داخلی),
 * each a curated subset of the permission catalog; this step turns the chosen
 * ones into real clinic roles.
 *
 * It runs AFTER the clinic exists and after the token refresh, deliberately:
 * `roleTemplates` and the seeding mutation are both clinic-scoped, and asking
 * for either with a token that predates the clinic is how this 403s on its
 * first real user.
 *
 * ── Partial failure is the state this rewrite exists for ────────────────────
 *
 * The first version fired five sequential `createClinicRole` mutations and
 * reported the set with one boolean. If the third failed, the person saw one
 * error, pressed «ساخت ۵ نقش» again, and created a SECOND «منشی» and a second
 * «پرستار» — each call minted a fresh `roleKey`, so nothing deduplicated them.
 * The clinic then opened with duplicate roles that its owner had to find and
 * delete before hiring anyone.
 *
 * So: each tile carries its own outcome, the seeding mutation is idempotent by
 * template key on the server, and a retry sends only what has not been created.
 * A tile that succeeded is locked — there is nothing left to decide about it.
 *
 * ── Nothing here is mandatory, and now it says what that costs ──────────────
 *
 * «فعلاً نه» was one click from a clinic in which every future hire lands as
 * `ISSUE.NO_ROLES` on «همکاران», and the copy mentioned only that roles are
 * editable later. Skipping is still supported — it is a head start, not a
 * decision the clinic is locked into — but the consequence is now stated, and
 * confirmed, because it is the kind of thing a person only discovers a week
 * later when they try to add their receptionist.
 */

/** The idempotent seeder. Skips a template the clinic already has. */
const SEED_ROLES = `mutation SeedClinicRoles($templateKeys: [String!]!) {
  seedClinicRoles(templateKeys: $templateKeys) { id nameFa roleKey }
}`;

/** The per-template fallback, for a BFF that does not yet serve the seeder. */
const CREATE_ROLE = `mutation CreateClinicRole($input: ClinicRoleInput!) {
  createClinicRole(input: $input) { id nameFa }
}`;

const EXISTING_ROLES = `query ListClinicRoles { listClinicRoles { id nameFa roleKey } }`;

/** «ویزیت» — an upsert keyed on (catalogId, doctorId), so a retry cannot duplicate it. */
const DECLARE_VISIT = `mutation SetClinicServiceDeclaration($input: ClinicServiceDeclarationInput!) {
  setClinicServiceDeclaration(input: $input) { id catalogId unitPrice isActive }
}`;

const STATUS = { IDLE: 'idle', PENDING: 'pending', DONE: 'done', FAILED: 'failed' };

/**
 * @param {object} props
 * @param {(setup: {
 *   doctor: {presences: object[], visitDurationMinutes: number}|null,
 *   visit: {priceMinor: number, durationMinutes: number},
 * }) => void} props.onDone
 *   called once the roles and «ویزیت» are written; `doctor` is the founder's
 *   hours on the doctor track and `null` otherwise, `visit` what «ویزیت» was
 *   declared with, for the wizard to activate it under the founder
 * @param {boolean} [props.doctorTrack] show and collect the founder-doctor's hours
 * @param {string} [props.currency] the NEW clinic's currency — not the panel's
 *   current one, which during registration is whatever clinic the founder came from
 * @param {number[]} [props.presenceDays] the founder's default working days (wire
 *   indices, 0 = شنبه) — شنبه تا چهارشنبه unless their number is from abroad
 */
export default function ClinicRolesStep({
  onDone, onStaleAccess, disabled, doctorTrack = false, currency = DEFAULT_CURRENCY,
  presenceDays = DEFAULT_PRESENCE_DAYS,
}) {
  const {
    t, has, formatNumber, formatDigits, formatList, weekdayNames, fromDisplayAmount,
  } = useTranslation();
  // The default price is a TOMAN figure, so it is a default only for an IRR
  // clinic. Any other currency types its own — 600,000 read as dollars is not a
  // default, it is a mistake the founder would have to notice.
  const irr = currency === DEFAULT_CURRENCY;
  const { isMobile } = useResponsive();

  const [useDefaults, setUseDefaults] = React.useState(true);
  const [presences, setPresences] = React.useState(() => defaultPresences(presenceDays));
  const [duration, setDuration] = React.useState(String(DEFAULT_VISIT_MINUTES));
  // In the clinic's DISPLAY units (IRR: toman), as MoneyField reports them.
  const [price, setPrice] = React.useState(irr ? String(DEFAULT_VISIT_PRICE_TOMAN) : '');
  // «ویزیت» is written once; a retry after a later failure does not rewrite it.
  const [visitDone, setVisitDone] = React.useState(false);
  const [visitFailed, setVisitFailed] = React.useState(false);

  const [templates, setTemplates] = React.useState([]);
  const [picked, setPicked] = React.useState(() => new Set());
  const [status, setStatus] = React.useState({});      // templateKey → STATUS
  const [rowError, setRowError] = React.useState({});  // templateKey → message
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [confirmSkip, setConfirmSkip] = React.useState(false);
  const alertRef = React.useRef(null);

  const load = React.useCallback(() => {
    setLoading(true);
    setLoadError('');
    let alive = true;
    bffQuery(PERMISSION_CATALOG_QUERY, {}, 'PermissionCatalog')
      .then((d) => {
        if (!alive) return;
        const list = d?.roleTemplates || [];
        setTemplates(list);
        // Everything ticked to begin with: the common case is a clinic that
        // wants the standard set, and unticking two is less work than ticking
        // three. Skipping entirely is still one button away.
        setPicked(new Set(list.map((x) => x.key)));
      })
      .catch((e) => {
        if (!alive) return;
        // A failed load is NOT an empty list (packages/DESIGN.md rule 6). An
        // empty tile grid here would say «your clinic needs no roles», which is
        // both false and unrecoverable.
        const described = describeError(e, 'roles', t);
        setLoadError(described.message);
        if (described.recovery === RECOVERY.REFRESH_ACCESS) onStaleAccess?.();
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [t, onStaleAccess]);

  React.useEffect(() => load(), [load]);

  React.useEffect(() => {
    if (error?.message) alertRef.current?.focus();
  }, [error]);

  const toggle = (key) => {
    if (status[key] === STATUS.DONE) return;   // already created; nothing to decide
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  /**
   * Templates still to create: chosen, and not already done. With the defaults
   * on, «chosen» is every template — the tiles are not shown, so a tile
   * unticked before the tick went back on must not quietly drop a role.
   */
  const outstanding = templates.filter((x) => (useDefaults || picked.has(x.key)) && status[x.key] !== STATUS.DONE);
  // The doctor's half, when edited by hand. An overlapping or backwards shift
  // is marked on its row by the editor; here it only holds the button, the same
  // way the People drawer refuses the save.
  const durationValue = Number(duration);
  const durationBad = !Number.isInteger(durationValue)
    || durationValue < DURATION_MIN || durationValue > DURATION_MAX;
  const hoursBad = Object.keys(presenceIssues(presences)).length > 0;
  const doctorInvalid = doctorTrack && !useDefaults && (durationBad || hoursBad);
  // «ویزیت» at no price is a service that bills nothing — held like a bad shift.
  // Minor units of the clinic's currency — whole rials for IRR — which is what
  // accounting stores. Asked with the tick on too when there is no default.
  const askPrice = !useDefaults || !irr;
  const visitPriceMinor = fromDisplayAmount(
    askPrice ? price : String(DEFAULT_VISIT_PRICE_TOMAN), currency,
  ) || 0;
  const priceBad = askPrice && !(visitPriceMinor > 0);
  const editsInvalid = doctorInvalid || priceBad;

  const failedCount = templates.filter((x) => status[x.key] === STATUS.FAILED).length;
  const anyDone = templates.some((x) => status[x.key] === STATUS.DONE);

  /**
   * Create the outstanding roles.
   *
   * Prefers the server-side seeder, which is idempotent by template key and
   * needs one round trip. Falls back to per-template creation — reading the
   * clinic's existing roles first, so a retry after a partial failure still
   * cannot duplicate — for a BFF that has not shipped the seeder yet.
   */
  const submit = async () => {
    if (editsInvalid) return;
    if (!outstanding.length) { finish(); return; }
    setBusy(true);
    setError(null);
    setRowError({});
    setStatus((prev) => {
      const next = { ...prev };
      outstanding.forEach((x) => { next[x.key] = STATUS.PENDING; });
      return next;
    });

    try {
      const created = await bffMutation(
        SEED_ROLES,
        { templateKeys: outstanding.map((x) => x.key) },
        'SeedClinicRoles',
      );
      // The seeder answers with what the clinic HAS, which includes rows a
      // previous attempt already created and this call skipped — so everything
      // asked for is now present, whether this call made it or found it.
      setStatus((prev) => {
        const next = { ...prev };
        outstanding.forEach((x) => { next[x.key] = STATUS.DONE; });
        return next;
      });
      void created;
      setBusy(false);
      finish();
      return;
    } catch (seedErr) {
      // `GRAPHQL_VALIDATION_FAILED` means this BFF predates the seeder; any
      // other code is a real refusal and must not be retried field-by-field.
      const code = seedErr?.extensions?.code || '';
      const unsupported = code === 'GRAPHQL_VALIDATION_FAILED'
        || /Cannot query field|Unknown (?:type|argument)|seedClinicRoles/i.test(seedErr?.message || '');
      if (!unsupported) {
        const described = describeError(seedErr, 'roles', t);
        setStatus((prev) => {
          const next = { ...prev };
          outstanding.forEach((x) => { next[x.key] = STATUS.FAILED; });
          return next;
        });
        setError(described);
        if (described.recovery === RECOVERY.REFRESH_ACCESS) onStaleAccess?.();
        setBusy(false);
        return;
      }
    }

    // ── Fallback path ────────────────────────────────────────────────────────
    let existing = new Set();
    try {
      const rows = await bffQuery(EXISTING_ROLES, {}, 'ListClinicRoles');
      existing = new Set((rows?.listClinicRoles || []).map((r) => String(r.nameFa || '').trim()));
    } catch { /* an unreadable role list only costs the dedupe, not the seeding */ }

    let lastError = null;
    for (const tpl of outstanding) {
      if (existing.has(String(tpl.nameFa || '').trim())) {
        setStatus((prev) => ({ ...prev, [tpl.key]: STATUS.DONE }));
        continue;
      }
      try {
        // Sequential: each role is a Keycloak composite write, and firing them
        // in parallel is how the sync ends up half-applied.
        await bffMutation(
          CREATE_ROLE,
          { input: { nameFa: tpl.nameFa, permissions: tpl.permissions } },
          'CreateClinicRole',
        );
        setStatus((prev) => ({ ...prev, [tpl.key]: STATUS.DONE }));
      } catch (e) {
        const described = describeError(e, 'roles', t);
        lastError = described;
        setStatus((prev) => ({ ...prev, [tpl.key]: STATUS.FAILED }));
        setRowError((prev) => ({ ...prev, [tpl.key]: described.message }));
        if (described.recovery === RECOVERY.REFRESH_ACCESS) break;
      }
    }

    setBusy(false);
    if (lastError) {
      setError(lastError);
      if (lastError.recovery === RECOVERY.REFRESH_ACCESS) onStaleAccess?.();
      return;
    }
    finish();
  };

  /**
   * Declare «ویزیت», then hand everything to the wizard.
   *
   * Reached from every way out of the step — the primary, «فعلاً نه», and the
   * way past a failed template load — because skipping ROLES is not skipping
   * the visit: a clinic without its visit service cannot bill the first
   * patient, which is a worse surprise than a missing receptionist role.
   * A failure stays on this step with its reason and the same button retries.
   */
  const collect = () => {
    const doctor = !doctorTrack ? null : (useDefaults ? defaultDoctorSetup(presenceDays) : {
      presences: toPresenceInput(presences),
      visitDurationMinutes: durationValue,
    });
    const visit = {
      priceMinor: visitPriceMinor,
      durationMinutes: doctor?.visitDurationMinutes || DEFAULT_VISIT_MINUTES,
      // Stored as the service's name: Persian «ویزیت» in the Iranian market,
      // the panel's own word anywhere else (parsinaDefaults.visitDeclarationInput).
      ...(isIranMarket() ? {} : { name: t('common.entity.visit') }),
    };
    return { doctor, visit };
  };

  const finish = async () => {
    const { doctor, visit } = collect();
    if (!visitDone) {
      setBusy(true);
      setError(null);
      try {
        await bffMutation(DECLARE_VISIT, { input: visitDeclarationInput(visit) }, 'SetClinicServiceDeclaration');
        setVisitDone(true);
        setVisitFailed(false);
      } catch (e) {
        setVisitFailed(true);
        const described = describeError(e, 'roles', t);
        setError({ ...described, message: t('auth.setup.visitFailed', { reason: described.message }) });
        if (described.recovery === RECOVERY.REFRESH_ACCESS) onStaleAccess?.();
        setBusy(false);
        return;
      }
      setBusy(false);
    }
    onDone({ doctor, visit });
  };

  /**
   * The way past a visit that would not save. The clinic exists and its owner
   * must be able to reach it — the same rule the failed template load follows —
   * so a failed «ویزیت» offers a retry AND a way on; «کالاها و خدمات» declares
   * it later exactly as this step would have. `visit: null` tells the wizard
   * there is nothing to switch on.
   */
  const proceedWithoutVisit = () => {
    setError(null);
    onDone({ doctor: collect().doctor, visit: null });
  };

  const blocked = busy || loading || disabled || editsInvalid;

  useShortcut('ctrl+s', () => { if (!blocked) submit(); }, {
    label: t('auth.roles.confirmShort'),
    group: t('shortcuts.groups.onboarding'),
    enabled: !blocked,
  });

  const primaryLabel = () => {
    if (busy) return t('common.state.saving');
    if (failedCount) return t('auth.roles.retryFailed', { count: formatNumber(failedCount) });
    if (useDefaults) return t('auth.setup.apply');
    if (!outstanding.length) return t('auth.roles.continueNone');
    return t('auth.roles.confirm', { count: formatNumber(outstanding.length) });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: STACK.regular }}>
      <StepHeading id="setup-heading" title={t('auth.setup.title')} subtitle={t('auth.setup.explainer')} />

      {/* ── «تنظیمات پیش‌فرض پارسینا» ────────────────────────────────────
          On by default. The tick decides WHICH surface is shown — a summary of
          what will be written, or the editors — and never loses what was
          edited: taking it off again reopens the editors as they were left. */}
      <Checkbox
        checked={useDefaults}
        onChange={() => setUseDefaults((v) => !v)}
        disabled={busy || disabled}
        data-testid="setup-use-defaults"
        style={{
          padding: '12px 14px', borderRadius: RADIUS.card, minHeight: 44,
          background: useDefaults ? soft(C.teal, 0.06) : C.card,
          border: `1.5px solid ${useDefaults ? C.teal : C.border}`,
        }}
        label={(
          <span style={{ display: 'block', minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: '0.84375rem', fontWeight: 700, color: C.textPrimary }}>
              {t('auth.setup.useDefaults')}
            </span>
            <span style={{ display: 'block', fontSize: '0.71875rem', color: C.textMuted, marginTop: 2 }}>
              {t(useDefaults ? 'auth.setup.useDefaultsOnHint' : 'auth.setup.useDefaultsOffHint')}
            </span>
          </span>
        )}
      />

      {useDefaults && !loadError ? (
        <ul
          data-testid="setup-defaults-summary"
          style={{
            listStyle: 'none', margin: 0, padding: 0,
            display: 'flex', flexDirection: 'column', gap: STACK.tight,
          }}
        >
          <SummaryLine icon={<ShieldCheck size={15} color={C.teal} aria-hidden="true" />}>
            {loading
              ? <SkeletonText width="60%" height={11} />
              : t('auth.setup.summary.roles', {
                names: formatList(templates.map((tpl) => roleTemplateLabel(tpl, t, has))),
              })}
          </SummaryLine>
          {irr ? (
            <SummaryLine icon={<Receipt size={15} color={C.teal} aria-hidden="true" />}>
              {t('auth.setup.summary.visit', { price: formatNumber(DEFAULT_VISIT_PRICE_TOMAN) })}
            </SummaryLine>
          ) : null}
          {doctorTrack ? (
            <>
              <SummaryLine icon={<Clock size={15} color={C.teal} aria-hidden="true" />}>
                {t('auth.setup.summary.hours', {
                  fromDay: weekdayNames()[presenceDays[0]],
                  toDay: weekdayNames()[presenceDays[presenceDays.length - 1]],
                  start: formatDigits(DEFAULT_SHIFT.startTime),
                  end: formatDigits(DEFAULT_SHIFT.endTime),
                })}
              </SummaryLine>
              <SummaryLine icon={<Clock size={15} color={C.teal} aria-hidden="true" />}>
                {t('auth.setup.summary.duration', { minutes: formatNumber(DEFAULT_VISIT_MINUTES) })}
              </SummaryLine>
            </>
          ) : null}
        </ul>
      ) : null}

      {!useDefaults && !loadError ? (
        <SectionHeading id="roles-heading" title={t('auth.roles.title')} subtitle={t('auth.roles.explainer')} />
      ) : null}

      {loading && !useDefaults ? (
        // Kept as role-card-shaped blocks rather than a `SkeletonList`: a role
        // card is a bordered 2-up tile with a name and a caption, and a list
        // row is neither. Announced once for the whole grid.
        <SkeletonRegion
          label={t('common.state.loading')}
          style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))',
          gap: 10,
          }}
          data-testid="roles-loading">
          {[0, 1, 2, 3, 4].map((i) => (
            <div
              key={i}
              style={{
                border: `1.5px solid ${C.border}`, borderRadius: RADIUS.card,
                padding: '12px 14px', minHeight: 44,
                display: 'flex', flexDirection: 'column', gap: 6, justifyContent: 'center',
              }}
            >
              <SkeletonText width="55%" height={13} />
              <SkeletonText width="35%" height={10} />
            </div>
          ))}
        </SkeletonRegion>
      ) : null}

      {!loading && loadError ? (
        /* «بارگذاری مجدد», not «تلاش مجدد». When the load failed because the
           token is stale, the wizard is ALSO showing an access-refresh offer
           above this — and two buttons reading «تلاش مجدد» that do different
           things is worse than either of them alone. This one reloads the list;
           that one re-adopts the grants. */
        <ErrorState
          title={t('errors.roles.loadFailedTitle')}
          description={loadError}
          onRetry={load}
          retryLabel={t('common.actions.reload')}
        />
      ) : null}

      {!loading && !loadError && !useDefaults ? (
        <fieldset
          style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}
          aria-labelledby="roles-heading"
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))',
              gap: 10,
            }}
          >
            {templates.map((tpl) => {
              const on = picked.has(tpl.key);
              const state = status[tpl.key] || STATUS.IDLE;
              const done = state === STATUS.DONE;
              const failed = state === STATUS.FAILED;
              return (
                /* The kit `Checkbox` IS the tile. It renders a native <label>
                   wrapping one Radix control and forwards `className`/rest onto
                   that label, which is exactly the "picker row" shape its own
                   docstring describes.

                   The previous version wrapped it in an outer <button> and hid
                   the control with `tabIndex={-1}`: a button inside a button,
                   announced twice, with the checked state invisible to
                   assistive tech. Wrapping it in a <label> instead would have
                   been the same mistake in a second costume — nested labels —
                   so the tile is the checkbox rather than a container for it. */
                <Checkbox
                  key={tpl.key}
                  checked={on}
                  onChange={() => toggle(tpl.key)}
                  disabled={done || blocked}
                  data-testid={`role-tile-${tpl.key}`}
                  data-state={state}
                  style={{
                    padding: '12px 14px', borderRadius: RADIUS.card,
                    minHeight: 44, minWidth: 0,
                    background: failed
                      ? soft(C.red, 0.05)
                      : on ? soft(C.navy, 0.05) : C.card,
                    border: `1.5px solid ${failed ? C.red : on ? C.navy : C.border}`,
                    transition: 'border-color .15s, background .15s',
                    opacity: done ? 0.75 : 1,
                  }}
                  label={(
                    <span style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{
                          display: 'block', fontSize: '0.84375rem', fontWeight: 700, color: C.textPrimary,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {roleTemplateLabel(tpl, t, has)}
                        </span>
                        <span
                          style={{ display: 'block', fontSize: '0.71875rem', color: failed ? C.red : C.textMuted, marginTop: 2 }}
                        >
                          {failed
                            ? (rowError[tpl.key] || t('auth.roles.status.failed'))
                            : done
                              ? t('auth.roles.status.done')
                              : t('people.defaultRoles.permissionCount', { count: formatNumber(tpl.permissions.length) })}
                        </span>
                      </span>
                      <span aria-hidden="true" style={{ flexShrink: 0, display: 'inline-flex' }}>
                        {state === STATUS.PENDING ? <Spinner size={14} label={t('common.state.loading')} /> : null}
                        {done ? <Check size={16} color={C.green} /> : null}
                        {failed ? <TriangleAlert size={16} color={C.red} /> : null}
                      </span>
                    </span>
                  )}
                />
              );
            })}
          </div>
        </fieldset>
      ) : null}

      {!loading && !loadError && !useDefaults ? (
        <>
          <p style={{ margin: 0, fontSize: '0.75rem', color: C.textMuted, lineHeight: 1.8 }}>
            {t('auth.roles.changeableLater')}
          </p>
          {/* The cost of «فعلاً نه», said before it is pressed rather than
              discovered a week later at the first hire. */}
          <Alert variant="warning" icon={<AlertTriangle size={15} aria-hidden="true" />}>
            {t('auth.roles.skipWarning')}
          </Alert>
        </>
      ) : null}

      {/* ── «ویزیت» ────────────────────────────────────────────────────────
          On both tracks: every clinic gets its visit service, and this is the
          one thing about it worth deciding now. Everything else about it —
          performers, insurance, commission — is «خدمات»' business. Shown with
          the tick on as well when the clinic's currency has no default. */}
      {askPrice ? (
        <section
          aria-labelledby="visit-heading"
          data-testid="setup-visit"
          style={{ display: 'flex', flexDirection: 'column', gap: STACK.tight }}
        >
          <SectionHeading id="visit-heading" title={t('auth.setup.visitTitle')} subtitle={t('auth.setup.visitHint')} />
          <MoneyField
            testid="setup-visit-price"
            currency={currency}
            label={t('auth.setup.visitPrice')}
            value={price}
            onChange={setPrice}
            disabled={busy || disabled || visitDone}
            error={priceBad ? t('auth.setup.visitPriceRequired') : ''}
          />
        </section>
      ) : null}

      {/* ── The founder-doctor's own hours ─────────────────────────────────
          The same two editors as the doctor's row on «همکاران», so what is
          set here is exactly what that screen will show and edit later. */}
      {doctorTrack && !useDefaults ? (
        <section
          aria-label={t('auth.setup.hoursTitle')}
          data-testid="setup-doctor-hours"
          style={{ display: 'flex', flexDirection: 'column', gap: STACK.regular }}
        >
          {/* No heading of its own: `PresenceEditor` carries «ساعات حضور در
              کلینیک», and a second one above it read as a stutter. */}
          <PresenceEditor value={presences} onChange={setPresences} disabled={busy || disabled} />
          <VisitDurationField value={duration} onChange={setDuration} disabled={busy || disabled} />
        </section>
      ) : null}

      {error?.message ? (
        <div ref={alertRef} tabIndex={-1} style={{ outline: 'none' }}>
          <Alert variant="error" role="alert" data-testid="roles-error">{error.message}</Alert>
        </div>
      ) : null}

      {visitFailed && !busy ? (
        <PrimaryAction secondary onClick={proceedWithoutVisit} data-testid="setup-skip-visit">
          {t('auth.setup.continueWithoutVisit')}
        </PrimaryAction>
      ) : null}

      {!loadError ? (
        <div style={{ display: 'flex', gap: 10, marginTop: 4, flexWrap: 'wrap' }}>
          {/* The doctor wizard's pair: outline secondary, gradient primary that
              takes the rest of the row. */}
          {/* With the defaults on there is nothing to skip — the tick IS the
              choice — unless a seeding attempt already landed some roles, when
              «ادامه» walks on with what exists. */}
          {!useDefaults || anyDone ? (
            <PrimaryAction
              secondary
              onClick={() => (anyDone ? finish() : setConfirmSkip(true))}
              disabled={busy || disabled || editsInvalid}
              data-testid="roles-skip"
            >
              {anyDone ? t('common.actions.continue') : t('auth.roles.skip')}
            </PrimaryAction>
          ) : null}
          <PrimaryAction
            onClick={submit}
            disabled={blocked}
            aria-busy={busy || undefined}
            aria-keyshortcuts="Control+S"
            data-testid="roles-submit"
            style={{ flex: 1, minWidth: 200 }}
          >
            {busy ? <Spinner size={14} color="#fff" label={t('common.state.loading')} /> : null}
            {primaryLabel()}
            {busy ? null : <Kbd combo="ctrl+s" />}
          </PrimaryAction>
        </div>
      ) : (
        // Even when the templates cannot be read, the clinic exists and its
        // owner must be able to reach it. A load failure here must never be a
        // wall between a person and the panel they just created.
        <PrimaryAction secondary onClick={finish} data-testid="roles-skip">
          {t('auth.roles.skip')}
        </PrimaryAction>
      )}

      <ConfirmDialog
        open={confirmSkip}
        onOpenChange={setConfirmSkip}
        title={t('auth.roles.skipConfirmTitle')}
        description={t('auth.roles.skipWarning')}
        confirmLabel={t('auth.roles.skip')}
        cancelLabel={t('common.actions.cancel')}
        onConfirm={() => { setConfirmSkip(false); finish(); }}
      />
    </div>
  );
}

/** A heading INSIDE the step — one level below the step's own `StepHeading`. */
function SectionHeading({ id, title, subtitle }) {
  return (
    <div>
      <h3 id={id} style={{ margin: '0 0 2px', fontSize: '0.84375rem', fontWeight: 700, color: C.textPrimary }}>{title}</h3>
      {subtitle ? (
        <div style={{ fontSize: '0.75rem', color: C.textSecondary, lineHeight: 1.8 }}>{subtitle}</div>
      ) : null}
    </div>
  );
}

/** One line of what the defaults will write. */
function SummaryLine({ icon, children }) {
  return (
    <li style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.78125rem', color: C.textSecondary, lineHeight: 1.8 }}>
      <span style={{ flexShrink: 0, display: 'inline-flex' }}>{icon}</span>
      <span style={{ flex: 1, minWidth: 0 }}>{children}</span>
    </li>
  );
}
